<template>
  <Teleport to="body">
    <Transition name="site-update">
      <aside
        v-if="visible"
        role="status"
        aria-live="polite"
        class="fixed bottom-4 left-1/2 z-[140] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-2xl bg-black p-4 text-white shadow-2xl ring-1 ring-white/15 sm:bottom-6 sm:left-auto sm:right-6 sm:translate-x-0"
      >
        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10">
            <RefreshCw class="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold leading-tight">{{ isFr ? 'Mise à jour disponible' : 'Update available' }}</p>
            <p class="truncate text-xs text-white/60">{{ subtitle }}</p>
          </div>
          <button
            type="button"
            class="h-9 shrink-0 whitespace-nowrap rounded-full bg-white px-4 text-sm font-semibold text-gray-900 transition hover:bg-white/90"
            @click="reload"
          >
            {{ isFr ? 'Actualiser' : 'Refresh' }}
          </button>
          <button
            type="button"
            class="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/60 transition hover:bg-white/10 hover:text-white"
            :aria-label="isFr ? 'Fermer' : 'Dismiss'"
            @click="dismiss"
          >
            <X class="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div v-if="counting" class="mt-3 flex items-center gap-3">
          <div class="h-1 flex-1 overflow-hidden rounded-full bg-white/15" role="progressbar" aria-valuemin="0" :aria-valuemax="COUNTDOWN_S" :aria-valuenow="remaining">
            <div class="h-full rounded-full bg-emerald-400 transition-[width] duration-1000 ease-linear" :style="{ width: `${(remaining / COUNTDOWN_S) * 100}%` }" />
          </div>
          <button type="button" class="shrink-0 whitespace-nowrap text-xs font-medium text-white/70 underline-offset-2 transition hover:text-white hover:underline" @click="dismiss">
            {{ isFr ? 'Annuler' : 'Cancel' }}
          </button>
        </div>

        <div class="mt-3 flex items-center justify-between gap-3 border-t border-white/10 pt-3">
          <span id="auto-refresh-label" class="whitespace-nowrap text-xs text-white/70">{{ isFr ? 'Toujours actualiser automatiquement' : 'Always refresh automatically' }}</span>
          <button
            type="button"
            role="switch"
            :aria-checked="autoRefresh"
            aria-labelledby="auto-refresh-label"
            :class="['relative h-6 w-11 shrink-0 rounded-full transition-colors', autoRefresh ? 'bg-emerald-500' : 'bg-white/20']"
            @click="toggleAuto"
          >
            <span :class="['absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform', autoRefresh ? 'translate-x-5' : 'translate-x-0']" />
          </button>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { RefreshCw, X } from 'lucide-vue-next'

const COUNTDOWN_S = 10
const RELOAD_COOLDOWN_KEY = 'kendrick-update-reload-at'
const RELOAD_COOLDOWN_MS = 30_000
const STORAGE_KEY = 'kendrick-dismissed-site-update-version'

const { locale } = useI18n()
const isFr = computed(() => locale.value === 'fr')
const state = useAppUpdateState()
const autoRefresh = useState('site-auto-refresh', () => false)
const dismissedId = ref('')
const editing = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

const visible = computed(() => state.value.available && dismissedId.value !== state.value.latestId)

const remaining = ref(COUNTDOWN_S)
const counting = ref(false)
const subtitle = computed(() => {
  if (editing.value) return isFr.value ? 'Terminez votre saisie.' : 'Finish typing first.'
  if (counting.value) return isFr.value ? `Actualisation dans ${remaining.value} s` : `Refreshing in ${remaining.value}s`
  return isFr.value ? 'Nouvelle version prête.' : 'New version ready.'
})

const recentlyReloaded = () => {
  try { return Date.now() - Number(sessionStorage.getItem(RELOAD_COOLDOWN_KEY) || 0) < RELOAD_COOLDOWN_MS } catch { return false }
}

const reload = () => {
  try { sessionStorage.setItem(RELOAD_COOLDOWN_KEY, String(Date.now())) } catch { /* storage unavailable */ }
  window.location.reload()
}

const dismiss = () => {
  dismissedId.value = state.value.latestId
  try { window.localStorage.setItem(STORAGE_KEY, dismissedId.value) } catch { /* storage unavailable */ }
}

// Turning the switch on while an update waits applies it now, unless the visitor is typing.
const toggleAuto = () => {
  autoRefresh.value = !autoRefresh.value
  try { window.localStorage.setItem(AUTO_REFRESH_KEY, autoRefresh.value ? '1' : '0') } catch { /* storage unavailable */ }
  if (autoRefresh.value && !isUserEditing()) reload()
  else tick()
}

// One-second tick while the popin is visible: re-checks the guards, drives the countdown.
// Typing or unsaved work stops it; Cancel (dismiss) snoozes until the next version.
const tick = () => {
  editing.value = isUserEditing()
  const canCount = autoRefresh.value && !editing.value && !recentlyReloaded()
  if (!canCount) {
    counting.value = false
    remaining.value = COUNTDOWN_S
    return
  }
  if (!counting.value) {
    counting.value = true
    remaining.value = COUNTDOWN_S
    return
  }
  remaining.value -= 1
  if (remaining.value <= 0) reload()
}

watch(visible, (value) => {
  if (timer) clearInterval(timer)
  timer = null
  counting.value = false
  remaining.value = COUNTDOWN_S
  if (!value) return
  tick()
  timer = setInterval(tick, 1000)
}, { immediate: true })

onMounted(() => {
  try { dismissedId.value = window.localStorage.getItem(STORAGE_KEY) || '' } catch { /* storage unavailable */ }
})
onBeforeUnmount(() => { if (timer) clearInterval(timer) })
</script>

<style scoped>
.site-update-enter-active,
.site-update-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.site-update-enter-from,
.site-update-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
</style>
